// Cognito ID tokens are valid for 60 minutes. Renew once fewer than this many seconds remain so a
// token never reaches the API Gateway authorizer after its `exp`.
export const ID_TOKEN_RENEWAL_MARGIN_SECONDS = 5 * 60;

function base64UrlDecode(value) {
	let base64 = value.replace(/-/g, "+").replace(/_/g, "/");
	while (base64.length % 4) {
		base64 += "=";
	}

	const binary = atob(base64);
	// Claims can contain UTF-8 (names, emails); atob returns one character per byte.
	return decodeURIComponent(binary.split("").map(c => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""));
}

/**
 * Decode a JWT payload without verifying it. Returns null for anything that isn't a JWT.
 * @param {string} token
 */
export function decodeJwtPayload(token) {
	if (typeof token !== "string") {
		return null;
	}

	const parts = token.split(".");
	if (parts.length !== 3 || !parts[1]) {
		return null;
	}

	try {
		const payload = JSON.parse(base64UrlDecode(parts[1]));
		return payload && typeof payload === "object" ? payload : null;
	} catch (e) {
		return null;
	}
}

/**
 * Seconds until the token's `exp`, or null if the token has no readable `exp`.
 * @param {string} token
 * @param {object} options
 * @param {number} options.nowMs local clock, defaults to Date.now()
 * @param {number} options.clockDriftSeconds local clock minus Cognito's clock (CognitoUserSession.getClockDrift())
 */
export function getJwtSecondsRemaining(token, { nowMs = Date.now(), clockDriftSeconds = 0 } = {}) {
	const payload = decodeJwtPayload(token);
	const exp = payload ? Number(payload.exp) : NaN;
	if (!Number.isFinite(exp)) {
		return null;
	}

	const drift = Number.isFinite(clockDriftSeconds) ? clockDriftSeconds : 0;
	return exp - (Math.floor(nowMs / 1000) - drift);
}

/**
 * True when the token expires within the renewal margin. A token without a readable `exp`
 * (including no token at all) never needs renewal here; the regular refresh path owns that case.
 * The margin is capped at half the token's lifetime so a short-lived token isn't renewed on every call.
 * @param {string} token
 * @param {object} options
 */
export function jwtNeedsRenewal(token, { nowMs = Date.now(), clockDriftSeconds = 0, marginSeconds = ID_TOKEN_RENEWAL_MARGIN_SECONDS } = {}) {
	const remaining = getJwtSecondsRemaining(token, { nowMs, clockDriftSeconds });
	if (remaining === null) {
		return false;
	}

	const payload = decodeJwtPayload(token);
	const lifetime = Number(payload.exp) - Number(payload.iat);
	const margin = Number.isFinite(lifetime) && lifetime > 0 ? Math.min(marginSeconds, lifetime / 2) : marginSeconds;
	return remaining <= margin;
}
